/**
 * 3D Model Viewer with Scroll-Triggered Animations
 * 
 * This component creates an interactive 3D scene that responds to page scrolling.
 * Think of it like a digital flipbook where scrolling through the page makes
 * the 3D model move and animate, creating an engaging storytelling experience.
 * 
 * Key Features:
 * - Loads 3D models (GLTF/GLB files) with optional compression
 * - Syncs model animations to page scroll position
 * - Provides camera controls for user interaction
 * - Automatically adjusts to different screen sizes
 * - Supports multiple lighting setups for realistic rendering
 * 
 * Performance Notes:
 * - Uses efficient WebGL rendering through Three.js
 * - Implements smooth interpolation for animations
 * - Optimizes memory usage with proper cleanup
 * 
 * @author CAN
 * @version 1.0.0
 */

import * as THREE from "three";
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

/**
 * Custom HTML element that renders a 3D model with scroll-triggered animations
 * 
 * This is like creating a custom HTML tag (like <video> or <img>) but for 3D content.
 * When you use <model-viewer> in your HTML, this class handles all the 3D magic.
 */
class ModelViewer extends HTMLElement {
  constructor() {
    super();
    this.initializeSettings();
    this.initializeInterpolation();
    
    // Bind methods to avoid creating new functions every frame
    this.boundAnimate = this.animate.bind(this);
    this.boundResizeHandler = this.handleResize.bind(this);
    this.boundScrollHandler = this.updateScrollProgress.bind(this);
    
    // Performance tracking
    this.needsUpdate = false;
    this.isVisible = false;
    
    // Track materials for proper cleanup
    this.trackedMaterials = new Set();
    this.trackedGeometries = new Set();
    
    // Loading state
    this.isLoaded = false;
    this.loadingManager = null;
    this.loadingProgress = 0;
    this.loadingOverlay = null;
  }

  /**
   * Sets up all the configuration options for the 3D viewer
   * 
   * This reads settings from a JSON script tag in your HTML, like a recipe
   * that tells the viewer how to behave. It's similar to how you might
   * configure a camera app with different settings.
   */
  initializeSettings() {
    // Find the canvas element where we'll draw our 3D scene
    this.canvas = this.querySelector('canvas');
    
    // Read configuration from a hidden script tag in your HTML
    // This keeps your 3D settings separate from your JavaScript code
    this.settingsJson = this.querySelector('script[type="application/json"][data-settings]')?.textContent || "{}";
    this.settings = JSON.parse(this.settingsJson || "{}");

    // Basic identification and debugging
    this.sectionId = this.settings.sectionId;
    this.section = document.getElementById(this.sectionId);
    this.debug = !!this.settings.debug;
    this.debugScrollProgress = this.querySelector('span[data-scroll-progress]');
    this.enableDracoDecompression = !!this.settings.enableDracoDecompression;

    // Animation behavior settings
    // 'none': Model stays still while scrolling
    // 'animate-half': Model animates through first 50% of its animation (old setting due to some models containing animations that reverse back to the start)
    // 'animate-full': Model animates through 100% of its animation
    this.scrollBehaviour = this.settings?.animation?.scrollBehaviour || 'none';

    // Camera and interaction settings
    this.enableOrbitControls = !!this.settings?.camera?.enableOrbitControls; // (!! is a boolean check on stringified values)
    this.autoRotateSpeed = (this.settings?.camera?.autoRotateSpeed ?? 0) / 10;
    this.modelUrl = this.settings?.model?.modelFile;
    this.cameraSettings = this.settings?.camera || {};
    this.modelSettings = this.settings?.model || { xPos: 0, yPos: 0, scale: 1 };
    this.sceneSettings = this.settings?.scene || {};
    
    // Lighting configuration arrays
    // These control how bright and colorful your 3D scene appears
    this.ambientLights = this.settings?.lights?.ambientLights || [];
    this.directionalLights = this.settings?.lights?.directionalLights || [];
    this.hemisphereLights = this.settings?.lights?.hemisphereLights || [];
    this.spotLights = this.settings?.lights?.spotLights || [];
    this.pointLights = this.settings?.lights?.pointLights || [];

    // Scaling factor for positioning (converts percentage values to actual 3D units)
    this.modelFactor = this.settings?.modelFactor ?? 100;
    
    this.dullMeshesOpacity = 1;
  }

  /**
   * Sets up smooth animation transitions
   * 
   * Instead of jumping directly to animation positions, this creates
   * smooth, gradual movements that look more natural and professional.
   * Think of it like easing into a parking spot instead of slamming on the brakes.
   */
  initializeInterpolation() {
    this.interpolation = {
      currentProgress: 0,    // Where the animation currently is
      targetProgress: 0      // Where we want the animation to go
    };
  }
  
  /**
   * Check if the element is visible in the viewport
   * 
   * This helps optimize performance by only rendering when the 3D scene
   * is actually visible to the user.
   */
  isElementVisible() {
    const rect = this.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    
    return (
      rect.top < windowHeight && 
      rect.bottom > 0 && 
      rect.left < windowWidth && 
      rect.right > 0
    );
  }

  /**
   * Called when this custom element is added to the page
   * 
   * This is like the "power on" moment for your 3D viewer.
   * It sets up the 3D world, loads your model, and starts the animation loop.
   */
  connectedCallback() {
    if (!this.canvas) return;
    
    this.setupGSAP();
    this.setupLoadingManager();
    this.createLoadingOverlay();
    this.setupScene();
    this.loadModel();
    this.handleResize();
    this.setupControls();
    this.animate();
    
    // Add camera helper for debugging (shows camera position and direction)
    if (this.debug) {
      this.scene.add(new THREE.CameraHelper(this.camera));
    }

    if (this.debugScrollProgress) {
      this.debugScrollProgress.textContent = this.scrollProgress;
    }
  }
  
  setupGSAP() {
    gsap.registerPlugin(ScrollTrigger)
    gsap.ticker.lagSmoothing(1000, 16);
    gsap.ticker.fps(60);
  }
  
  /**
   * Sets up Three.js LoadingManager to track asset loading progress
   * 
   * This creates a loading manager that monitors the progress of all assets
   * being loaded (models, textures, etc.) and provides callbacks for progress
   * updates and completion. Think of it like a download manager that tracks
   * how much of your files have been downloaded.
   */
  setupLoadingManager() {
    this.loadingManager = new THREE.LoadingManager();
    
    // Called when an individual item finishes loading
    this.loadingManager.onLoad = () => {
      if (this.debug) {
        console.log('All assets loaded successfully');
      }
      this.onLoadingComplete();
    };
    
    // Called during loading progress
    this.loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
      this.loadingProgress = itemsLoaded / itemsTotal;
      
      if (this.debug) {
        console.log(`Please wait - loading 3D model: ${Math.round(this.loadingProgress * 100)}% (${itemsLoaded}/${itemsTotal})`);
      }
      
      this.updateLoadingProgress();
    };
    
    // Called if an error occurs during loading
    this.loadingManager.onError = (url) => {
      console.error('Failed to load:', url);
      this.onLoadingError(url);
    };
  }
  
  /**
   * Creates a simple loading overlay with progress indicator
   */
  createLoadingOverlay() {
    if (this.settings?.loading?.disabled) return;
    
    this.loadingOverlay = document.createElement('div');
    this.loadingOverlay.innerHTML = `
      <div style="
        position: absolute;
        top: 0; left: 0;
        width: 100%; height: 100%;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        color: white;
        font-family: Arial, sans-serif;
      ">
        <div style="text-align: center;">
          <div style="margin-bottom: 20px; font-size: 18px;">
            ${this.settings?.loading?.text || 'Please wait - loading 3D model'}
          </div>
          <div style="
            width: 200px;
            height: 4px;
            background: rgba(255,255,255,0.2);
            border-radius: 2px;
            overflow: hidden;
            margin-bottom: 10px;
          ">
            <div class="progress-bar" style="
              width: 0%;
              height: 100%;
              background: white;
              transition: width 0.3s ease;
            "></div>
          </div>
          <div class="progress-text" style="font-size: 14px; opacity: 0.8;">0%</div>
        </div>
      </div>
    `;
    
    this.progressBar = this.loadingOverlay.querySelector('.progress-bar');
    this.progressText = this.loadingOverlay.querySelector('.progress-text');
    this.appendChild(this.loadingOverlay);
  }
  
  updateLoadingProgress() {
    if (!this.progressBar || !this.progressText) return;
    const percentage = Math.round(this.loadingProgress * 100);
    this.progressBar.style.width = `${percentage}%`;
    this.progressText.textContent = `${percentage}%`;
  }
  
  onLoadingComplete() {
    this.isLoaded = true;
    
    if (this.loadingOverlay) {
      this.loadingOverlay.style.opacity = '0';
      this.loadingOverlay.style.transition = 'opacity 0.5s ease-out';
      setTimeout(() => {
        if (this.loadingOverlay) {
          this.loadingOverlay.remove();
          this.loadingOverlay = null;
        }
      }, 500);
    }
    
    this.setupScrollAnimation();
  }
  
  onLoadingError(url) {
    if (this.loadingOverlay) {
      this.loadingOverlay.innerHTML = `
        <div style="
          position: absolute; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0, 0, 0, 0.8); display: flex; align-items: center;
          justify-content: center; z-index: 1000; color: #ff6b6b;
          font-family: Arial, sans-serif; text-align: center;
        ">
          <div>
            <div style="font-size: 18px; margin-bottom: 10px;">Failed to load 3D model</div>
            <div style="font-size: 14px; opacity: 0.8;">Please check the model path</div>
          </div>
        </div>
      `;
    }
    console.error('Failed to load 3D model:', url);
  }
  

  /**
   * Cleanup when the element is removed from the page
   * 
   * This prevents memory leaks by removing event listeners and
   * disposing of 3D objects properly. Think of it like cleaning up
   * after a party - you don't want to leave a mess behind.
   */
  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Creates the 3D world where your model will live
   * 
   * This is like setting up a movie set - you need lights, cameras,
   * and a stage before you can start filming.
   */
  setupScene() {
    this.scene = new THREE.Scene();
    this.setupRenderer();
    this.setupCamera();
    this.setupLights();
  }

  /**
   * Removes all event listeners and disposes of 3D resources
   * 
   * This is crucial for preventing memory leaks, especially on mobile devices
   * where memory is limited. Think of it like properly closing applications
   * instead of just minimizing them.
   */
  cleanup() {
    // Remove event listeners
    window.removeEventListener('resize', this.boundResizeHandler);
    window.removeEventListener('scroll', this.boundScrollHandler);
    
    // Remove UI control event listeners
    if (this.zoomSlider) {
      this.zoomSlider.removeEventListener('input', this.zoomHandler);
    }
    if (this.orbitControlsToggle) {
      this.orbitControlsToggle.removeEventListener('change', this.orbitHandler);
    }
    
    // Dispose of Three.js objects properly
    this.controls?.dispose?.();
    
    // Dispose of all geometries and materials
    this.scene?.traverse((object) => {
      if (object.geometry) {
        object.geometry.dispose();
        this.trackedGeometries.delete(object.geometry);
      }
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach(material => {
            material.dispose();
            this.trackedMaterials.delete(material);
          });
        } else {
          object.material.dispose();
          this.trackedMaterials.delete(object.material);
        }
      }
    });
    
    // Dispose of renderer and force context loss
    this.renderer?.dispose();
    this.renderer?.forceContextLoss();
    this.scene?.clear();
    
    // Clean up checkpoint timelines
    if (this.checkpointTimelines) {
      this.checkpointTimelines.forEach(timeline => {
        if (timeline && timeline.scrollTrigger) {
          timeline.scrollTrigger.kill();
        }
      });
      this.checkpointTimelines = [];
    }
    
    // Clear tracked objects
    this.trackedMaterials.clear();
    this.trackedGeometries.clear();
    
    // Clean up loading overlay
    if (this.loadingOverlay) {
      this.loadingOverlay.remove();
    }
    
    // Clear references
    this.model = null;
    this.gltf = null;
    this.animations = null;
    this.mixer = null;
    this.drivingAction = null;
    this.otherActions = null;
    this.loadingManager = null;
    this.loadingOverlay = null;
    this.scrollAnimationSetup = false;
  }

  /**
   * Sets up the WebGL renderer that draws your 3D scene
   * 
   * The renderer is like a very powerful paintbrush that can draw
   * complex 3D shapes in real-time. WebGL is the technology that
   * makes this possible in web browsers.
   */
  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: false,
      precision: 'mediump', // Use medium precision for better performance
      powerPreference: 'high-performance', // Prefer discrete GPU
      preserveDrawingBuffer: false, // Don't preserve drawing buffer
    });
    
    // Set pixel ratio with performance consideration
    const pixelRatio = Math.min(window.devicePixelRatio, 2); // Cap at 2x for performance
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    
    // Use sRGB color space for more realistic lighting
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    
    // Enable frustum culling for better performance
    this.renderer.frustumCulled = true;
    
    // Set background color from scene settings or default to black
    const backgroundColor = this.sceneSettings?.backgroundColor || '#000000';
    this.renderer.setClearColor(this.hexToThreeJSColor(backgroundColor), 1);
  }
  
  /**
   * Converts percentage values to actual 3D world units
   * 
   * This is like converting "50% of the way across the room" to
   * actual feet or meters. The modelFactor determines how big
   * your 3D world is relative to the percentages in your settings.
   */
  cameraSettingsCalc(value) {
    return (value / 100) * this.modelFactor;
  }

  /**
   * Converts hex color string (e.g., '#000000') to Three.js color format (0x000000)
   * 
   * @param {string} hexColor - Hex color string like '#000000' or '#fff'
   * @returns {number} Three.js color value like 0x000000
   */
  hexToThreeJSColor(hexColor) {
    // Remove the # if present
    const hex = hexColor.replace('#', '');
    
    // Handle 3-character hex codes (e.g., '#fff' -> '#ffffff')
    const fullHex = hex.length === 3 
      ? hex.split('').map(char => char + char).join('')
      : hex;
    
    // Convert to Three.js format (0xRRGGBB)
    return parseInt(fullHex, 16);
  }

  /**
   * Sets up the virtual camera that determines your view of the 3D world
   * 
   * Think of this like setting up a camera for filming. You need to decide:
   * - Where to put the camera (position)
   * - Which way to point it (rotation)
   * - How much to zoom in/out
   * - What field of view to use (how wide the camera sees)
   */
  setupCamera() {
    // Extract camera settings with sensible defaults
    const {
      fov = 45,              // Field of view (how wide the camera sees)
      zoom = 1,              // Zoom level (1 = normal, 2 = 2x zoom)
      distance = 100,        // How far the camera is from the center
      positionX = 0,         // Left/right position
      positionY = 0,         // Up/down position
    } = this.cameraSettings;

    // Calculate aspect ratio (width divided by height)
    const aspect = window.innerWidth / window.innerHeight;
    const near = 0.1;       // Closest thing the camera can see
    const far = 1000;        // Farthest thing the camera can see
    
    // Create the camera with calculated settings
    this.camera = new THREE.PerspectiveCamera(fov, aspect, near, far);
    
    // Convert percentage settings to actual 3D positions
    const posX = this.cameraSettingsCalc(positionX);
    const posY = this.cameraSettingsCalc(positionY);
    const posZ = this.cameraSettingsCalc(distance);
    
    // Position the camera in 3D space
    this.camera.position.set(posX, posY, posZ);
    this.camera.zoom = zoom;
    
    this.initialCameraState = {
      position: this.camera.position.clone(),
      zoom: this.camera.zoom,
    };

    this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    
    // Apply all the camera changes
    this.camera.updateProjectionMatrix();
    
    // Store the initial camera state for later restoration
    this.initialCameraState = {
      position: this.camera.position.clone(),
      zoom: this.camera.zoom,
      fov: this.camera.fov,
    };
    
    // Debug logging
    if (this.debug) {
      console.log('Initial camera setup:', {
        settings: { positionX, positionY, distance, zoom, fov },
        calculatedPosition: { posX, posY, posZ },
        finalPosition: this.camera.position.toArray(),
        initialState: this.initialCameraState
      });
    }
    
    // Set up orbit controls if enabled
    if (this.enableOrbitControls) {
      this.setupOrbitControls();
    }
  }
  
  /**
   * Sets up all the lights in the 3D scene
   * 
   * Lighting is crucial for making 3D objects look realistic.
   * Think of it like setting up studio lights for photography -
   * you need different types of lights to create depth and atmosphere.
   */
  setupLights() {
    // Create ambient lights that provide overall illumination
    // Ambient light is like the soft, even lighting you get on a cloudy day.
    // It fills in shadows and provides a base level of brightness
    // so objects don't appear completely dark.
    this.ambientLights.forEach(lightConfig => {
      const light = new THREE.AmbientLight(lightConfig.color, lightConfig.intensity);
      this.scene.add(light);
    });

    // Create directional lights that cast shadows and create depth
    // Directional light is like sunlight - it comes from one direction
    // and creates strong shadows. This gives objects their 3D shape
    // and makes them look solid rather than flat.
    this.directionalLights.forEach(lightConfig => {
      const light = new THREE.DirectionalLight(lightConfig.color, lightConfig.intensity);
      light.position.set(lightConfig.posX, lightConfig.posY, lightConfig.posZ);  
      this.scene.add(light);
      
      if (this.debug) {
        const helper = new THREE.DirectionalLightHelper(light, 5);
        this.scene.add(helper);
      }
    });

    // Create hemisphere lights for realistic outdoor lighting
    // Hemisphere light simulates the sky above and ground below.
    // The top gets one color (like blue sky) and the bottom gets
    // another color (like green grass), creating natural-looking lighting.
    this.hemisphereLights.forEach(lightConfig => {
      const light = new THREE.HemisphereLight(lightConfig.skyColor, lightConfig.groundColor, lightConfig.intensity);
      light.position.set(lightConfig.posX, lightConfig.posY, lightConfig.posZ);
      this.scene.add(light);
      
      if (this.debug) {
        const helper = new THREE.HemisphereLightHelper(light, 5);
        this.scene.add(helper);
      }
    });
    
    this.spotLights.forEach(lightConfig => {
      const light = new THREE.SpotLight(lightConfig.color, lightConfig.intensity, lightConfig.angle, lightConfig.penumbra, lightConfig.distance, lightConfig.decay);
      light.position.set(lightConfig.posX, lightConfig.posY, lightConfig.posZ);
      light.angle = lightConfig.angle;
      light.penumbra = lightConfig.penumbra;
      light.distance = lightConfig.distance;
      light.decay = lightConfig.decay;
      this.scene.add(light);
      
      if (this.debug) {
        const helper = new THREE.SpotLightHelper(light, 0xff0000);
        this.scene.add(helper);
      }
    });
    
    this.pointLights.forEach(lightConfig => {
      const light = new THREE.PointLight(lightConfig.color, lightConfig.intensity, lightConfig.distance, lightConfig.decay);
      light.position.set(lightConfig.posX, lightConfig.posY, lightConfig.posZ);
      light.distance = lightConfig.distance;
      light.decay = lightConfig.decay;
      this.scene.add(light);
      
      if (this.debug) {
        const helper = new THREE.PointLightHelper(light, 5);
        this.scene.add(helper);
      }
    });
  }

  /**
   * Sets up all user interaction controls for the 3D viewer
   * 
   * This consolidates all control-related setup into one place:
   * - Custom UI controls (zoom slider, orbit controls toggle)
   * - Mouse/touch camera controls (OrbitControls)
   * - Auto-rotation settings
   * 
   * Think of it like setting up all the remote control features
   * for your 3D experience in one organized place.
   */
  setupControls() {
    // Set up OrbitControls if enabled in settings
    if (this.enableOrbitControls) {
      this.setupOrbitControls();
    }

    // Set up custom UI controls (zoom slider, orbit controls toggle)
    this.setupCustomControls();
  }

  /**
   * Sets up custom UI controls for the 3D viewer
   * 
   * This creates interactive elements like zoom sliders and toggle buttons
   * that let users control the 3D experience. Think of it like adding
   * remote control buttons to your TV.
   */
  setupCustomControls() {
    const controls = document.getElementById('model-controls-ui');
    if (!controls) return;
    
    // Set CSS variable for control panel height
    controls.parentElement?.style?.setProperty('--model-controls-ui-height', `${controls.offsetHeight}px`);

    this.setupZoomControls(controls);
    this.setupOrbitControlsToggle(controls);
  }
  
  /**
   * Creates a zoom slider that lets users zoom in/out of the 3D scene
   * 
   * This is like having a zoom lens on a camera - you can get closer
   * to see details or pull back to see the big picture.
   */
  setupZoomControls(controls) {
    const zoomSlider = controls.querySelector('input[type="range"]');
    if (!zoomSlider) return;

    // Store reference for cleanup
    this.zoomSlider = zoomSlider;

    // Calculate zoom range based on current camera zoom
    const initialZoom = this.camera.zoom;
    const min = initialZoom * 0.5;      // Can zoom out to 50% of current view
    const max = initialZoom * 2;         // Can zoom in to 200% of current view
    const stepSize = (max - min) / 100;  // 100 steps for smooth control

    zoomSlider.step = stepSize.toFixed(5);

    // Create bound handler for proper cleanup
    this.zoomHandler = () => {
      const percentage = parseInt(zoomSlider.value, 10) || 0;
      const newZoom = min + (percentage / 100) * (max - min);
      
      this.camera.zoom = newZoom;
      this.camera.updateProjectionMatrix();
      this.needsUpdate = true; // Mark for update
    };

    // Update camera zoom when slider moves
    zoomSlider.addEventListener('input', this.zoomHandler);
  }
  
  /**
   * Creates a toggle button for enabling/disabling mouse/touch camera controls
   * 
   * When enabled, users can drag to rotate the view and pinch to zoom.
   * When disabled, the camera stays in its fixed position.
   */
  setupOrbitControlsToggle(controls) {
    const orbitControlsToggle = controls.querySelector('input[data-orbit-controls-toggle]');
    if (!orbitControlsToggle) return;
    
    // Store reference for cleanup
    this.orbitControlsToggle = orbitControlsToggle;
    
    // If orbit controls weren't enabled in settings, keep UI in sync but do nothing
    if (!this.controls) {
      orbitControlsToggle.checked = false;
      this.canvas.style.pointerEvents = 'none';
      return;
    }

    // Set initial state
    this.controls.enabled = orbitControlsToggle.checked;
    this.controls.autoRotate = orbitControlsToggle.checked && this.autoRotateSpeed > 0;
    this.canvas.style.pointerEvents = orbitControlsToggle.checked ? 'auto' : 'none';
    
    // Create bound handler for proper cleanup
    this.orbitHandler = (e) => {
      const checked = !!e.target.checked;
      
      if (this.debug) {
        console.log(`Before ${checked ? 'enabling' : 'disabling'} OrbitControls:`, {
          position: this.camera.position.toArray(),
          zoom: this.camera.zoom,
          fov: this.camera.fov
        });
      }
      
      // Enable/disable orbit controls
      this.controls.enabled = checked;
      this.controls.autoRotate = checked && this.autoRotateSpeed > 0;
      this.canvas.style.pointerEvents = checked ? 'auto' : 'none';
      this.needsUpdate = true; // Mark for update
      
      if (this.debug) {   
        // Log position differences for debugging
        if (this.initialCameraState) {
          console.log('Initial stored position:', this.initialCameraState.position.toArray());
          console.log('Current position:', this.camera.position.toArray());
          console.log('Position difference:', {
            x: Math.abs(this.initialCameraState.position.x - this.camera.position.x),
            y: Math.abs(this.initialCameraState.position.y - this.camera.position.y),
            z: Math.abs(this.initialCameraState.position.z - this.camera.position.z)
          });
        }
      }
    };
    
    // Handle toggle changes
    orbitControlsToggle.addEventListener('change', this.orbitHandler);
  }
  
  /**
   * Sets up mouse/touch controls for camera movement
   * 
   * OrbitControls lets users interact with the 3D scene by:
   * - Dragging to rotate the view around the model
   * - Scrolling to zoom in/out
   * - Automatically rotating if enabled
   * 
   * Think of it like having a camera on a tripod that you can
   * move around to get different angles of your subject.
   */
  setupOrbitControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    
    // Enable smooth damping for natural-feeling movement
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.25;
    
    // Disable zoom and pan since we have custom zoom controls
    this.controls.enableZoom = false;
    this.controls.enablePan = false;
    
    // Set up auto-rotation if specified
    this.controls.autoRotate = this.autoRotateSpeed > 0;
    this.controls.autoRotateSpeed = this.autoRotateSpeed;
    
    // Make the camera orbit around the center point (0,0,0)
    this.controls.target.set(0, 0, 0);
    
    // Ensure camera is looking at the target
    this.camera.lookAt(this.controls.target);
    
    // Store the initial position that OrbitControls should maintain
    this.controlsInitialPosition = this.camera.position.clone();
    
    // Apply all control settings
    this.controls.update();
  }

  /**
   * Loads the 3D model file into the scene
   * 
   * This is like opening a box and taking out a model kit.
   * The GLTF format is like a digital LEGO set - it contains
   * all the pieces and instructions for building your 3D model.
   */
  loadModel() {
    const loader = new GLTFLoader(this.loadingManager);
    
    // Enable Draco compression if specified (reduces file size)
    if (this.enableDracoDecompression) {
      const dracoLoader = new DRACOLoader(this.loadingManager);
      dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
      loader.setDRACOLoader(dracoLoader);
    }
    
    // Load the model file
    loader.load(
      this.modelUrl,
      (gltf) => {
        // Model loaded successfully
        this.model = gltf.scene;
        
        // Position the model according to settings
        this.model.position.set(
          (this.modelSettings.xPos / 100 * this.modelFactor) || 0,
          (this.modelSettings.yPos / 100 * this.modelFactor) || 0,
          (this.modelSettings.zPos / 100 * this.modelFactor) || 0
        );

        // Scale the model
        const s = this.modelSettings.scale ?? 1;
        this.model.scale.set(s, s, s);

        // Store the full GLTF data for scene switching
        this.gltf = gltf;
        
        // Store animations for later use
        this.animations = gltf.animations || []; 
        this.scene.add(this.model);
                
        // Set up animation system
        this.setupAnimations();
        this.setupCheckpoints();
                
        const matchingObjects = this.dullObjects;
        
        matchingObjects.forEach(object => {
          object.traverse((child) => {
            if (child.isMesh) {
              // Clone the material so this mesh is unique
              child.material = child.material.clone();
              this.trackedMaterials.add(child.material); // Track for cleanup
          
              // Make it semi-transparent
              child.material.transparent = true;
              child.material.opacity = 0.5;
              this.dullMeshesOpacity = child.material.opacity;
              
              // Kill reflections / lighting
              child.material.metalness = 0;
              child.material.roughness = 1;
              child.material.envMap = null;
            }
          });
        });
      },
      undefined, // Progress callback (not used)
      (error) => {
        console.error('Error loading model:', error);
      }
    );
  }
  
  get dullObjects() {
    const namesToProcess = this.settings.dullObject3DNames.split(',').map(name => name.trim());

    // Function to check if a name matches any of the patterns
    const matchesPattern = (name) => {
      return namesToProcess.some(pattern => {
        if (pattern.endsWith('*')) {
          // Wildcard matching - check if name starts with the pattern (without *)
          const prefix = pattern.slice(0, -1);
          return name.startsWith(prefix);
        } else {
          // Exact match
          return name === pattern;
        }
      });
    };
    
    // Find all matching objects in the scene
    const matchingObjects = [];
    this.gltf.scene.traverse((child) => {
      if (matchesPattern(child.name)) {
        matchingObjects.push(child);
      }
    });

    return matchingObjects;
  }
  
  setupCheckpoints() {
    this.checkpoints = this.settings?.checkpoints || [];
    
    // Store all checkpoint timelines for potential cleanup
    this.checkpointTimelines = [];

    for (const checkpoint of this.checkpoints) {
      const timeline = this.startCheckpoint(checkpoint);
      if (timeline) {
        this.checkpointTimelines.push(timeline);
      }
    }
  }
  
  startCheckpoint(checkpoint) {    
    const _this = this;
    const checkpointElement = this.querySelector(`[data-checkpoint="${checkpoint.id}"]`);
    
    if (!checkpointElement) {
      console.warn(`Checkpoint element not found for ID: ${checkpoint.id}`);
      return null;
    }
  
    // Convert percentage settings to actual 3D positions
    const posX = this.cameraSettingsCalc(checkpoint.camera.positionX);
    const posY = this.cameraSettingsCalc(checkpoint.camera.positionY);
    const posZ = this.cameraSettingsCalc(checkpoint.camera.distance);    
    const zoom = checkpoint.camera.zoom;
  
    // Store initial state for perfect reversion
    const initialCamera = {
      x: this.camera.position.x,
      y: this.camera.position.y,
      z: this.camera.position.z,
      zoom: this.camera.zoom
    };
    
    const sectionHeight = this.section.offsetHeight;
    const startPx = sectionHeight * (checkpoint.startProgress / 100);
    const endPx = sectionHeight * (checkpoint.endProgress / 100);
    const animateBackOnEnd = checkpoint.animateBackOnEnd;
      
    if (this.debug) {
      console.log({    
        checkpoint: checkpoint.id,
        start: `top+=${startPx} top`,
        end: `top+=${endPx} top`
      })
    }
      
    const enter = gsap.timeline({
      scrollTrigger: {
        trigger: checkpointElement,
        start: `top+=${startPx} top`,
        end: `top+=${endPx} top`,
        scrub: true,
        markers: this.debug ? true : false,
      }
    });
    
    let dullMeshes = [];
    if (checkpoint.makeDullObject3DTransparent) {
      const matchingObjects = this.dullObjects;
      
      // Prepare materials for animation
      matchingObjects.forEach(object => {
        object.traverse((child) => {
          if (child.isMesh) {
            child.material = child.material.clone();
            this.trackedMaterials.add(child.material); // Track for cleanup
            child.material.transparent = true;
            dullMeshes.push(child);
          }
        });
      });
    }
    
    const forwardDuration = 0.75; // forward animation occupies 75% of timeline
    const returnStart = forwardDuration + 0.1; // return starts after 10% scroll threshold
    
    // Animate forward to checkpoint
    enter.to(this.camera.position, { 
      x: posX, y: posY, z: posZ,             
      onUpdate: () => {
        this.camera.lookAt(0, 0, 0);
        this.camera.updateProjectionMatrix();
      },
      duration: forwardDuration
    })
    .to(this.camera, { zoom: zoom, onUpdate: () => this.camera.updateProjectionMatrix() }, '<')
    .to(checkpointElement, { 
      opacity: 1, 
      zIndex: 10, 
      left: `${checkpoint.placementX}%`, 
      top: `${checkpoint.placementY}%`,
      duration: forwardDuration,
    }, '<')
    .to({}, {
      duration: forwardDuration * 0.5,
      ease: "power2.out",
      onUpdate: function() {
        if (checkpoint.makeDullObject3DTransparent && dullMeshes.length > 0) {
          const progress = this.progress();
          dullMeshes.forEach(mesh => {
            mesh.material.opacity = _this.dullMeshesOpacity - (progress * _this.dullMeshesOpacity);
          });
        }
      }
    }, '<');
    
    if (animateBackOnEnd) {
      enter.to(checkpointElement, { 
        opacity: 0, 
        zIndex: 0, 
        left: '50%', 
        top: '50%',
        duration: 0.15
      }, returnStart)
      .to(this.camera, { zoom: initialCamera.zoom, onUpdate: () => this.camera.updateProjectionMatrix() }, '<')
      .to(this.camera.position, { 
        x: initialCamera.x, 
        y: initialCamera.y, 
        z: initialCamera.z, 
        onUpdate: () => {
          this.camera.lookAt(0, 0, 0);
          this.camera.updateProjectionMatrix();
        },
        duration: 0.15
      }, '<');
    } else {
      enter.to(checkpointElement, { 
        opacity: 0, 
        zIndex: 0, 
        left: '50%', 
        top: '50%',
        duration: 0.15
      }, returnStart);
    }
    
    return enter;
  }
  
  
  /**
   * Chooses which animation clip to use for scroll control
   * 
   * This is like picking which scene from a movie to play.
   * We prioritize by name, then by whether it has animation data,
   * and finally fall back to the longest animation available.
   */
  selectDrivingClip(animations, preferredName) {
    if (!animations?.length) return null;

    // 1) Try to find animation with the exact name specified
    if (preferredName) {
      const byName = animations.find(a => a.name === preferredName);
      if (byName) return byName;
    }
    
    // 2) Find first animation that actually has movement data
    const withTracks = animations.find(a => a.tracks && a.tracks.length > 0);
    if (withTracks) return withTracks;

    // 3) Fallback: use the longest animation available
    return animations.reduce((max, a) =>
      (a.duration > (max?.duration ?? 0) ? a : max), animations[0]);
  }
  
  /**
   * Sets up the animation system for the 3D model
   * 
   * This is like setting up a video player that can pause, play,
   * and scrub through different parts of the video. Instead of
   * playing normally, we freeze it and control the playback
   * manually based on scroll position.
   */
  setupAnimations() {
    if (!this.animations?.length) return;

    // Create animation mixer (the "video player" for 3D animations)
    this.mixer = new THREE.AnimationMixer(this.model);

    // Choose which animation to control with scrolling
    this.drivingClip = this.selectDrivingClip(this.animations, this.preferredClipName);
    if (!this.drivingClip) return;

    // Set up the main animation action with error handling
    try {
      this.drivingAction = this.mixer.clipAction(this.drivingClip);
      this.drivingAction.clampWhenFinished = true;  // Stop at end instead of looping
      this.drivingAction.setLoop(THREE.LoopOnce, 0); // Play once, don't repeat
      this.drivingAction.enabled = true;
      this.drivingAction.play();
      this.drivingAction.setEffectiveTimeScale(0); // Freeze time - we'll control it manually
    } catch (error) {
      console.warn('Failed to setup driving animation:', error);
      this.drivingAction = null;
    }

    // OPTIONAL: Sync other animations to the same progress
    // This creates a coordinated animation where multiple parts
    // of the model move together in sync
    this.otherActions = this.animations
      .filter(a => a !== this.drivingClip)
      .map(a => {
        try {
          const act = this.mixer.clipAction(a);
          act.clampWhenFinished = true;
          act.setLoop(THREE.LoopOnce, 0);
          act.enabled = true;
          act.play();
          act.setEffectiveTimeScale(0);
          return act;
        } catch (error) {
          console.warn(`Failed to setup animation "${a.name}":`, error);
          return null; // Return null for failed animations
        }
      })
      .filter(action => action !== null); // Remove failed animations
  }

  /**
   * Connects scrolling to animation progress
   * 
   * This is like creating a remote control where scrolling up/down
   * on the page controls how far through the 3D animation you are.
   * Scroll to the top = beginning of animation, scroll to bottom = end.
   * 
   * This method is now called after loading is complete to prevent
   * scroll animations from running before the model is ready.
   */
  setupScrollAnimation() {
    // Only set up if not already set up and model is loaded
    if (this.scrollAnimationSetup || !this.isLoaded) return;
    
    window.addEventListener('scroll', this.boundScrollHandler, { passive: true });
    this.scrollAnimationSetup = true;
    
    // Initialize once so we start from the correct pose
    this.updateScrollProgress();
    
    if (this.debug) {
      console.log('Scroll animation enabled');
    }
  }
  
  get scrollData() {
    if (!this.section) return;
    
    // Calculate how far we've scrolled through this section
    const rect = this.section.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const sectionHeight = this.section.offsetHeight;

    // Total scrollable distance (section height minus what's visible)
    const totalScrollDistance = Math.max(1, sectionHeight - viewportHeight);
    
    // How far into the section we've scrolled (negative when above, positive when below)
    const scrollPosition = -rect.top;
    
    return {
      totalScrollDistance,
      scrollPosition
    };
  }

  /**
   * Calculates how far through the animation to play based on scroll position
   * 
   * This converts your position on the page (like "50% down the page")
   * into a position in the 3D animation (like "50% through the animation").
   * The result is smooth, scroll-synchronized movement.
   */
  updateScrollProgress() {
    const { totalScrollDistance, scrollPosition } = this.scrollData;
    
    if (this.drivingAction && this.scrollBehaviour !== 'none') {
      // Calculate progress (0 = start, 1 = end)
      // Cap at 50% if using 'animate-half' mode
      const maxProgress = (this.scrollBehaviour === 'animate-half') ? 0.5 : 1;
      const p = Math.min(Math.max(scrollPosition / totalScrollDistance, 0), maxProgress);
      
      // Set target for smooth interpolation
      this.interpolation.targetProgress = p;
      this.needsUpdate = true; // Mark for update
    }

    if (this.debugScrollProgress) {
      this.debugScrollProgress.textContent = Math.round(this.interpolation.currentProgress * 100);
    }
  }

  /**
   * Handles window resize events
   * 
   * This ensures your 3D scene always fits the screen properly,
   * like how a responsive website adjusts to different screen sizes.
   */
  handleResize() {
    // Update camera aspect ratio for new dimensions
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    
    // Resize the renderer to match new window size
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.needsUpdate = true; // Mark for update
  }

  /**
   * Main animation loop that runs continuously
   * 
   * This is like the heartbeat of your 3D application. It runs
   * 60 times per second (or more on high-refresh displays) to:
   * - Update animations based on scroll position
   * - Handle user camera controls
   * - Draw the current frame to the screen
   * 
   * Performance Note: This method is called very frequently, so
   * keep the code inside efficient and avoid heavy operations.
   */
  animate() {
    if (!this.isConnected) return;
    
    // Check if element is visible before rendering
    const wasVisible = this.isVisible;
    this.isVisible = this.isElementVisible();
    
    // Only update if visible or if visibility changed (for cleanup)
    if (this.isVisible || wasVisible !== this.isVisible) {
      this.updateAnimationProgress();
      
      // Update OrbitControls if enabled
      if (this.controls && this.controls.enabled && this.enableOrbitControls) {
        this.controls.update();
        
        // If auto-rotate is disabled, ensure camera position stays consistent
        if (!this.controls.autoRotate && this.controlsInitialPosition) {
          // Only allow rotation around the target, not position changes
          const currentDistance = this.camera.position.length();
          const targetDistance = this.controlsInitialPosition.length();
          
          if (Math.abs(currentDistance - targetDistance) > 0.1) {
            // Normalize position to maintain distance
            this.camera.position.normalize().multiplyScalar(targetDistance);
          }
        }
      }
      
      // Only render if something changed or element is visible
      if (this.needsUpdate || this.isVisible) {
        this.renderer.render(this.scene, this.camera);
        this.needsUpdate = false;
      }
    }
    
    // Schedule the next frame
    requestAnimationFrame(this.boundAnimate);
  }

  /**
   * Updates animation progress based on scroll position
   * 
   * This is like scrubbing through a video timeline. Instead of
   * playing the animation normally, we jump to specific frames
   * based on how far the user has scrolled down the page.
   */
  updateAnimationProgress() {
    if (!this.model || !this.drivingAction || this.scrollBehaviour === 'none') return;

    // Smoothly ease towards target progress (creates natural movement)
    const progressDelta = this.interpolation.targetProgress - this.interpolation.currentProgress;
    
    // Only update if there's a meaningful change (performance optimization)
    if (Math.abs(progressDelta) > 0.001) {
      this.interpolation.currentProgress += progressDelta * 0.1; // 10% towards target each frame
      this.needsUpdate = true; // Mark for update
    }

    // Calculate animation time based on scroll behavior
    const halfFactor = (this.scrollBehaviour === 'animate-half') ? 0.5 : 1;
    const t = this.interpolation.currentProgress * this.drivingClip.duration * halfFactor;

    // Set the animation to the calculated time (scrubbing)
    this.drivingAction.time = Math.min(Math.max(t, 0), this.drivingClip.duration);

    // Keep any other animations in sync (optional)
    if (this.otherActions?.length) {
      this.otherActions.forEach(act => {
        const d = act.getClip().duration;
        const tt = Math.min(Math.max(this.interpolation.currentProgress * d * halfFactor, 0), d);
        act.time = tt;
      });
    }

    // Update the animation mixer at the frozen time (no delta)
    this.mixer.update(0);
  }
}

customElements.define('model-viewer', ModelViewer);